import { KLASOUZYTKI__LABELS } from "./constants";

const SOIL_CLASS_RE = /([IVX]+[ab]?z?)$/; // "IVa", "V", "VIz"

/**
 * "RIVa" -> "grunty orne (klasa IVa)"
 * "Ls" -> "lasy"
 * "IIIa" -> "klasa IIIa"
 */
function KLASOUZYTKI__describePart(part: string): string {
	const match = part.match(SOIL_CLASS_RE);
	if (match === null) {
		return KLASOUZYTKI__LABELS[part] ?? part;
	}

	const klasa = match[1];
	const symbol = part.slice(0, part.length - klasa.length);
	if (symbol === "") {
		return `klasa ${klasa}`;
	}

	const base = KLASOUZYTKI__LABELS[symbol] ?? symbol;
	return `${base} (klasa ${klasa})`;
}

/**
 * "S-RIVb" -> "sady / grunty orne (klasa IVb)"
 * "Lz/RVI" -> "grunty zadrzewione i zakrzewione / grunty orne (klasa VI)"
 */
function KLASOUZYTKI__decodeCode(code: string): string {
	const separator = code.includes("-") ? "-" : code.includes("/") ? "/" : null;
	if (separator === null) {
		return KLASOUZYTKI__describePart(code);
	}

	return code
		.split(separator)
		.map((part) => KLASOUZYTKI__describePart(part))
		.join(" / ");
}

/**
 * "Ls,RIVa" -> [{ code: "Ls", label: "lasy" }, { code: "RIVa", label: "grunty orne (klasa IVa)" }]
 * "['B' 'brak_uprawnień']" -> [{ code: "B", label: "tereny mieszkaniowe" }]
 * ",****" -> []
 */
function KLASOUZYTKI__parseValue(value: string): Array<{ code: string; label: string }> {
	if (value === "") {
		return [];
	}

	let raw = value;
	if (raw.includes("[") || raw.includes("brak_uprawnień")) {
		const quoted = raw.match(/'([^']*)'/g)?.map((segment) => segment.slice(1, -1)) ?? [];
		raw = quoted.filter((segment) => segment !== "brak_uprawnień").join(",");
	}

	return raw
		.split(",")
		.map((token) => token.trim())
		.filter((token) => token !== "" && token !== "****")
		.map((code) => ({ code, label: KLASOUZYTKI__decodeCode(code) }));
}

/**
 * "RIVa" -> "R"
 * "S-RIVb" -> "S"
 * "Lz/RVI" -> "Lz"
 * "Ls" -> "Ls"
 */
function KLASOUZYTKI__getBaseSymbol(code: string): string {
	const head = code.split(/[-/]/)[0];
	return head.replace(SOIL_CLASS_RE, "");
}

/**
 * "RIVa,RIVb,Ls" -> ["R", "Ls"]
 * "Br-RV, PsV, Lzr-PsV" -> ["Br", "Ps", "Lzr"]
 * "['B,Ba' 'brak_uprawnień']" -> ["B", "Ba"]
 * "****" -> []
 */
function KLASOUZYTKI__getBaseSymbols(value: string): string[] {
	const symbols = new Set<string>();

	for (const { code } of KLASOUZYTKI__parseValue(value)) {
		const base = KLASOUZYTKI__getBaseSymbol(code);

		if (base !== "") {
			symbols.add(base);
		}
	}
	return [...symbols];
}

export { KLASOUZYTKI__decodeCode, KLASOUZYTKI__getBaseSymbol, KLASOUZYTKI__getBaseSymbols, KLASOUZYTKI__parseValue };
